import { useState } from 'react';
import {
  ProductColumn,
  ProductColumnsIds,
  ProductGridItem,
  ProductPriceType,
  Product as ProductType
} from '@setvi/shared/interfaces';
import { Box, BoxProps, Divider } from '@material-ui/core';
import {
  SButton,
  SCheckbox,
  SETVIImage,
  SHighlightText,
  SText
} from '@setvi/shared/components';
import { ProductLabel, PromotionBadge } from 'componentsV2';
import { TextColor } from '@setvi/shared/enums';
import { PriceButton, Stock } from 'pages/products/components';
import {
  FullPrice,
  ListPrice
} from 'pages/products/components/products-list/components/product/components';
import {
  RoundCheckboxUnselect,
  RoundCheckboxSelected
} from '@setvi/shared/components/sicons';
import { MobileCard } from '../styled';
import { HeaderContainer, ImageContainer } from './styled';
import { AIProductMatch } from './components';
import { Statuses } from './components/statuses';
import { MobileProductsList } from '../../..';

export interface MobileProductCardProps {
  product: ProductType;
  priceType: ProductPriceType;
  gridConfig: ProductGridItem;
  filters: MobileProductsList['filters'];
  columns?: ProductColumn[];
  searchQuery?: string;
  checked: boolean;
  productPricesLoading?: boolean;
  aiSearchActive?: boolean;
  style?: BoxProps['style'];
  onProductClick?: (product: ProductType) => void;
  checkboxDisabled?: (product: ProductType) => boolean;
  onPromotionClick?: (product: ProductType) => void;
  onGetProductPrice?: (product: ProductType) => Promise<void>;
  onStockClick?: (product: ProductType) => void;
  handleCheck?: (isChecked: boolean, product: ProductType[]) => void;
}

export const MobileProductListCard = ({
  product,
  priceType,
  filters,
  gridConfig,
  columns,
  aiSearchActive,
  productPricesLoading,
  searchQuery,
  checked,
  style,
  onProductClick,
  onPromotionClick,
  onGetProductPrice,
  onStockClick,
  handleCheck,
  checkboxDisabled
}: MobileProductCardProps) => {
  const showManufacturer =
    !columns || columns.some(c => c.id === ProductColumnsIds.Manufacturer);
  const [singlePriceLoading, setSinglePriceLoading] = useState(false);

  return (
    <MobileCard style={style} onClick={() => onProductClick(product)}>
      <HeaderContainer>
        <Box display="flex" alignItems="center" gridGap="8px">
          <ImageContainer>
            <SETVIImage
              alt={product.name}
              src={
                product.imageUrl ||
                product.images?.[0]?.path ||
                '/images/product-placeholder.svg'
              }
              errorImage="/images/product-placeholder.svg"
              style={{
                objectFit: 'contain',
                maxWidth: '100%',
                maxHeight: '100%'
              }}
            />
          </ImageContainer>
          <SHighlightText
            value={product.name}
            searchQuery={searchQuery}
            weight="semibold"
            size="sm"
            noLineClamp
            fontColor={TextColor.Black}
          />
        </Box>
        {handleCheck && (
          <Box
            style={{ alignSelf: 'baseline' }}
            onClick={e => e.stopPropagation()}>
            <SCheckbox
              icon={<RoundCheckboxUnselect />}
              checkedIcon={<RoundCheckboxSelected />}
              onChange={e => handleCheck(e.target.checked, [product])}
              checked={checked}
              disabled={checkboxDisabled?.(product)}
            />
          </Box>
        )}
      </HeaderContainer>
      <Box display="flex" flexDirection="column" py={2}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gridGap="4px">
          <SText
            title={`SKU: ${product.sku}`}
            size="sm"
            fontColor={TextColor.Grey}
          />
          {gridConfig?.stock.show && (
            <Stock
              variant="body2"
              style={{ minWidth: 'max-content' }}
              inventory={product.inventory}
              isDropship={product.dropship}
              stockInfo={product?.stockInfo}
              isStockProduct={product?.isStockProduct}
              // Card, not a table row — hide rather than hyphenate.
              nonStockedDisplay="hide"
              selectedCustomer={filters?.customer?.value}
              onClick={() => onStockClick(product)}
            />
          )}
        </Box>
        {gridConfig?.manufacturerLeadTime && (
          <SText
            title={`Lead time: ${product?.custom?.leadTime ? `${product?.custom?.leadTime} Days` : '-'}`}
            size="sm"
          />
        )}
      </Box>
      <Divider />
      {showManufacturer && (
        <Box
          py="12px"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gridGap="8px">
          <Box display="flex" alignItems="center" gridGap="4px">
            <SText
              size="sm"
              title={`MFG: ${product?.vendor?.name || 'Not Available'}`}
            />
            {product.privateLabel && <ProductLabel />}
            {product?.vendorHierarchy > 0 && (
              <ProductLabel type="strategic-vendor" />
            )}
            {product?.promotions?.length > 0 && (
              <PromotionBadge onClick={() => onPromotionClick(product)} />
            )}
          </Box>
          {gridConfig?.manufacturerSku && product?.manufacturerSku && (
            <SText
              title={product?.manufacturerSku}
              size="sm"
              fontColor={TextColor.SlateGrey}
              style={{ minWidth: 'max-content' }}
              noLineClamp
            />
          )}
        </Box>
      )}
      <Divider />

      {priceType === ProductPriceType.Full && !!gridConfig?.pricing.length && (
        <Box mt={2}>
          <FullPrice
            fontSize="sm"
            product={product}
            loading={productPricesLoading}
            pricing={gridConfig?.pricing}
          />
          <Divider style={{ marginTop: 8 }} />
        </Box>
      )}
      {priceType === ProductPriceType.List && (
        <Box mt={2}>
          <ListPrice product={product} fontSize="sm" />
          <Divider style={{ marginTop: 8 }} />
        </Box>
      )}
      {priceType === ProductPriceType.Get && filters?.customer?.value && (
        <Box mt={2}>
          <Box display="flex" justifyContent="center" height={40}>
            <PriceButton
              price={product.price}
              onGetPrice={() => onGetProductPrice(product)}
            />
          </Box>
          <Divider style={{ marginTop: 8 }} />
        </Box>
      )}
      {priceType === ProductPriceType.GetFull && filters?.customer?.value && (
        <Box mt={2}>
          <Box display="flex" justifyContent="center">
            {product.price ? (
              <FullPrice
                fontSize="sm"
                product={product}
                pricing={gridConfig?.pricing}
              />
            ) : (
              <SButton
                loading={singlePriceLoading}
                onClick={async e => {
                  e.stopPropagation();
                  setSinglePriceLoading(true);
                  await onGetProductPrice(product);
                  setSinglePriceLoading(false);
                }}
                style={{ width: 'max-content' }}
                variant="outlined">
                Get Price
              </SButton>
            )}
          </Box>
          <Divider style={{ marginTop: 8 }} />
        </Box>
      )}

      <Box mt={2}>
        <Statuses product={product} />
      </Box>
      {aiSearchActive && (
        <AIProductMatch
          percentage={product?.percentage}
          reasoning={product?.reasoning}
        />
      )}
    </MobileCard>
  );
};
